import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { ManagedServicesSchema } from "@/components/schema"
import { PageFAQ } from "@/components/PageFAQ"
import { managedServicesAIOpsFAQs } from "@/data/faqData"

const challenges = [
    {
        num: "01",
        stat: "72%",
        statLabel: "of IT budgets spent on keeping lights on",
        title: "Reactive Firefighting",
        description: "Teams spend more time fixing outages than preventing them, leading to downtime and business disruption.",
        painPoints: ["Unplanned outages", "Slow MTTR", "Revenue loss"],
    },
    {
        num: "02",
        stat: "10K+",
        statLabel: "alerts per day in a typical enterprise",
        title: "Alert Fatigue",
        description: "Tool sprawl creates a flood of noise, making it impossible to identify root causes quickly.",
        painPoints: ["Signal vs noise", "Tool sprawl", "Missed incidents"],
    },
    {
        num: "03",
        stat: "40%",
        statLabel: "of ops tasks are repetitive and scriptable",
        title: "Manual Toil",
        description: "Reliance on manual scripts and ticket-based workflows slows down resolution and increases costs.",
        painPoints: ["Runbook fatigue", "Ticket backlogs", "Rising costs"],
    },
]

const approachSteps = [
    {
        num: "01",
        title: "Automated Remediation",
        description: "Self-healing agents resolve routine incidents instantly without human intervention.",
    },
    {
        num: "02",
        title: "Intelligent Prevention",
        description: "Predictive models identify anomalies and performance degradation before failures occur.",
    },
    {
        num: "03",
        title: "Continuous Optimization",
        description: "Ongoing analysis of resource usage to optimize cost and performance dynamically.",
    },
]

const deliverableRows = [
    [
        "24/7 Intelligent Monitoring",
        "Predictive Maintenance",
        "Automated Incident Response",
        "Security Operations",
        "Performance Optimization",
        "Compliance Reporting",
        "Capacity Planning",
        "SLA Management",
    ],
    [
        "AIOps Platform Setup",
        "Runbook Automation",
        "Root Cause Analysis",
        "Cost Optimization",
        "Patch Management",
        "Disaster Recovery",
        "Cloud Governance",
        "Observability Stack",
    ],
    [
        "Self-Healing Infrastructure",
        "Anomaly Detection",
        "Event Correlation",
        "Change Management",
        "Vendor Management",
        "NOC as a Service",
        "FinOps Dashboards",
        "24/7 War Room Support",
    ],
]

const ManagedServicesAIOps = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="AI-First Managed Services Built for Modern Enterprises | RSA Tech Group"
                description="We move enterprise operations from reactive support to predictive, self-healing systems powered by AI-native managed services and AIOps."
                image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2670&auto=format&fit=crop"
            />
            <ManagedServicesSchema />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Managed Services & AIOps
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            AI-First Managed Services{" "}
                            <span className="text-[#FDA400]">Built for Modern Enterprises</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We move enterprise operations from reactive support to predictive, self-healing systems powered by AI-native managed services and AIOps.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Talk to a Managed Services Expert <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* The Challenge */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            The Problem
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            The{" "}
                            <span className="text-[#FDA400]">Challenge</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Most enterprises spend more time keeping systems running than improving them.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {challenges.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/[0.10] group-hover:scale-125 transition-all duration-700" />
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="mb-6">
                                        <div className="text-5xl md:text-6xl font-black text-[#FDA400] leading-none mb-1">
                                            {item.stat}
                                        </div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
                                            {item.statLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.painPoints.map((point, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/60 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {point}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI-First Approach */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Our Methodology
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                                AI-First{" "}
                                <span className="text-[#FDA400]">Approach</span>
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-10">
                                We reduce manual intervention by replacing it with intelligent automation. By leveraging AIOps, we ingest data from your entire estate to predict issues before they impact users.
                            </p>

                            <div className="space-y-8">
                                {approachSteps.map((step) => (
                                    <div key={step.num} className="flex gap-5 group">
                                        <div className="w-12 h-12 bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#FDA400]">{step.num}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white tracking-tight mb-2">{step.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* System Health visualization */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#FDA400]/[0.03] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                                    <div className="text-xs text-slate-400 uppercase tracking-[0.2em] font-bold">Live System Health</div>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#FDA400] text-xs font-bold">Optimized</span>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    {[
                                        { label: "Availability", value: "99.99%", width: "95%", color: "bg-[#FDA400]" },
                                        { label: "Auto-Remediation Rate", value: "88%", width: "88%", color: "bg-[#1C2B6E]" },
                                        { label: "Cost Savings (MoM)", value: "42%", width: "42%", color: "bg-[#FDA400]/70" },
                                    ].map((metric, i) => (
                                        <div key={i}>
                                            <div className="flex justify-between text-xs mb-2">
                                                <span className="text-slate-400 font-medium">{metric.label}</span>
                                                <span className="text-white font-bold">{metric.value}</span>
                                            </div>
                                            <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                                <div className={`h-full ${metric.color} rounded-full`} style={{ width: metric.width }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
                                    {[
                                        { value: "2.4M", label: "Events/day" },
                                        { value: "<30s", label: "Avg MTTR" },
                                        { value: "0", label: "P1 Incidents" },
                                    ].map((stat, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-lg font-black text-[#FDA400]">{stat.value}</div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Deliver - Scrolling Marquee */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Deliverables
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            What We{" "}
                            <span className="text-[#FDA400]">Deliver</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            End-to-end managed services designed to keep your enterprise running, optimized, and ahead of issues.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 space-y-4">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

                    {deliverableRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="overflow-hidden">
                            <div
                                className="flex gap-4 w-max"
                                style={{
                                    animation: `${rowIndex % 2 === 0 ? "scrollLeft" : "scrollRight"} ${30 + rowIndex * 5}s linear infinite`,
                                }}
                            >
                                {[...row, ...row].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl border border-[#1C2B6E]/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:border-[#FDA400]/30 hover:shadow-[0_12px_40px_rgba(28,43,110,0.1)] transition-all duration-300 group cursor-default flex-shrink-0"
                                    >
                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-30 group-hover:animate-ping" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#1C2B6E] font-bold text-sm whitespace-nowrap group-hover:text-[#FDA400] transition-colors duration-300">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Enterprise Ready + Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Enterprise Ready */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Built for Scale
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Enterprise Ready{" "}
                            <span className="text-[#FDA400]">by Design</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Designed to integrate seamlessly without disrupting your existing operations.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-24">
                        {[
                            { num: "01", title: "Hybrid & Multi-Cloud", desc: "Native support for AWS, Azure, GCP, and on-premise infrastructure.", tags: ["AWS", "Azure", "GCP", "On-Prem"] },
                            { num: "02", title: "Zero-Trust Security", desc: "Strict governance, private AI models, and compliance-ready operations.", tags: ["SOC2", "GDPR", "HIPAA", "ISO 27001"] },
                        ].map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>
                                <div className="relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl font-black text-white tracking-tight mb-3">{item.title}</h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">{item.desc}</p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/[0.06]">
                                        {item.tags.map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-white/[0.04] text-slate-400 text-xs font-medium rounded-full border border-white/[0.08] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Engagement Model */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Strategy-Led{" "}
                            <span className="text-[#FDA400]">Engagement</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {[
                            { num: "01", title: "Assess & Align" },
                            { num: "02", title: "Design & Modernize" },
                            { num: "03", title: "Operate & Optimize" },
                        ].map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)]">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black text-white tracking-tight">
                                    {step.title}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <PageFAQ
                faqs={managedServicesAIOpsFAQs}
                title="Managed Services & AIOps FAQs"
                subtitle="Common questions about AI-powered managed services and operations"
                variant="muted"
            />

            {/* CTA */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Transform Your{" "}
                        <span className="text-[#FDA400]">Operations Today</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Ready to reduce costs and improve reliability with AI-native managed services?
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Talk to a Managed Services Expert <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default ManagedServicesAIOps
